import fetcher, { XCsrfHeaderName } from '@app/utils/fetcher';
import CheckoutBreadcrumbs from '@features/purchasing/components/CheckoutBreadcrumbs';
import ToCheckout from '@features/purchasing/my-basket/ToCheckout';
import type { ICheckoutStep } from '@features/purchasing/types';
import Loader from '@store/components/Loader';
import { emit } from '@store/events';
import useConfiguration from '@store/hooks/useConfiguration';
import ky from 'ky';
import useSWR from 'swr';
import Buttons from './Buttons';
import Lines from './Lines';
import Totals from './Totals';
import type { IGetResponse, ILinks, OnUpdateLine } from './types';

export interface MyBasketProps {
  basketId: string;
  title: string;
  basketName: string;
  steps: ICheckoutStep[];
  nextStepUrl: string;
  links?: ILinks;
  apiUrls: { get: string; setLine: string };
}

export default function Index(props: MyBasketProps) {
  const { xsrf } = useConfiguration();
  const { data, error, mutate } = useSWR(props.apiUrls.get, url => fetcher<IGetResponse>(url), {
    revalidateOnFocus: false,
    errorRetryCount: 0,
  });

  const onUpdateLine: OnUpdateLine = async ({ productId, optionId, unitCount }) => {
    const { basketId } = props;

    const fd = new FormData();
    fd.set('basketId', basketId);
    fd.set('productId', productId);
    fd.set('unitCount', `${unitCount}`);
    if (optionId) fd.set('optionId', optionId);

    const response = await ky.put(props.apiUrls.setLine, {
      body: fd,
      headers: { [XCsrfHeaderName]: xsrf },
      throwHttpErrors: false,
    });

    if (!response.ok) {
      console.error('Failed to set item: ' + (await response.text()));
      return;
    }

    const json = await response.json<object>();
    await mutate();
    if ('notification' in json && json.notification) emit.alert(json.notification);
    if ('widgetUrl' in json && json.widgetUrl) emit.basket.reload(json.widgetUrl);
    if ('analyticsEvent' in json && json.analyticsEvent)
      emit.basket.productChanged(json.analyticsEvent);
  };

  if (!data && !error) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader className="text-base text-primary" />
      </div>
    );
  }

  if (error) {
    return <></>;
  }

  return (
    <div className="max-w-5xl flex flex-col mx-auto">
      {props.steps && <CheckoutBreadcrumbs steps={props.steps} />}

      <h1 className="flex flex-col laptop:flex-row items-center laptop:items-baseline laptop:justify-center gap-x-2">
        <small className="text-primary-dark">{props.title}</small>
        {props.basketName}
      </h1>

      {props.links && (
        <Buttons
          {...props.links}
          basketName={props.basketName}
          hasLines={(data?.lines?.length ?? 0) > 0}
        />
      )}
      <Lines lines={data?.lines ?? []} deposits={data?.deposits ?? []} onUpdate={onUpdateLine} />
      {data && (
        <>
          <Totals
            summaryBeforeVat={data.summaryBeforeVat}
            summaryAfterVat={data.summaryAfterVat}
            summaryVat={data.summaryVat}
            vatLevels={data.vatLevels}
          />
          <ToCheckout hasLines={data.lines.length > 0} nextStepUrl={props.nextStepUrl} />
        </>
      )}
    </div>
  );
}
